import type { OwnershipGraph, OwnershipNode } from "./ownershipDomain";
import { toOwnershipList } from "./commonOwnershipDomain";
import type { FilterCondition, FilterField, FilterGroup } from "@/components-v2/filterDialogDomain";
import type { ColumnFilterConfig } from "./ownershipTableDomain";
import {
  ALL_OWNERSHIP_COLUMN_IDS,
  COLUMN_FILTER_CONFIGS,
  COLUMN_LABELS,
  OwnershipColumnId,
} from "./ownershipTableDomain";

export type OwnershipFilterCondition = FilterCondition<OwnershipColumnId>;

export type OwnershipFilterGroup = FilterGroup<OwnershipColumnId>;

export const applyFilterGroup = (
  rows: OwnershipNode[],
  filterGroup: OwnershipFilterGroup | undefined,
): OwnershipNode[] => {
  if (!filterGroup || filterGroup.conditions.length === 0) return rows;
  return rows.filter((row) => {
    const matches = (c: OwnershipFilterCondition) =>
      COLUMN_FILTER_CONFIGS[c.field]?.applyFilter(row, c.operator, c.value) ?? true;
    return filterGroup.logicalOperator === "or"
      ? filterGroup.conditions.some(matches)
      : filterGroup.conditions.every(matches);
  });
};

export type FilteredOwnershipGraph = OwnershipGraph & { idsConnectingToRoot: Set<string> };

const findShortestPathsToRoot = (graph: OwnershipGraph, rootId: string): Map<string, string[]> => {
  const neighbours = new Map<string, string[]>();
  graph.edges.forEach(({ source, target }) => {
    neighbours.set(source, [...(neighbours.get(source) ?? []), target]);
    neighbours.set(target, [...(neighbours.get(target) ?? []), source]);
  });
  const pathToRoot = new Map<string, string[]>([[rootId, []]]);
  const queue = [rootId];
  for (let i = 0; i < queue.length; i++) {
    const current = queue[i];
    const currentPath = [current, ...(pathToRoot.get(current) ?? [])];
    (neighbours.get(current) ?? [])
      .filter((next) => !pathToRoot.has(next))
      .forEach((next) => {
        pathToRoot.set(next, currentPath);
        queue.push(next);
      });
  }
  return pathToRoot;
};

const findIdsConnectingToRoot = (
  graph: OwnershipGraph,
  rootId: string,
  ids: Set<string>,
): Set<string> => {
  const pathToRoot = findShortestPathsToRoot(graph, rootId);
  return new Set(
    [...ids]
      .flatMap((id) => pathToRoot.get(id) ?? [])
      .filter((id) => id !== rootId && !ids.has(id)),
  );
};

export const filterOwnershipGraph = (
  graph: OwnershipGraph,
  filterGroup: OwnershipFilterGroup | undefined,
): FilteredOwnershipGraph => {
  const rootId = graph.nodes.find((node) => node.isRoot)?.id;
  if (!rootId || !filterGroup?.conditions.length) {
    return { ...graph, idsConnectingToRoot: new Set() };
  }
  const idsMatchingFilter = new Set(
    applyFilterGroup(toOwnershipList(graph), filterGroup).map((n) => n.id),
  );
  const idsConnectingToRoot = findIdsConnectingToRoot(graph, rootId, idsMatchingFilter);
  const shownIds = new Set([rootId, ...idsMatchingFilter, ...idsConnectingToRoot]);
  return {
    nodes: graph.nodes.filter((node) => shownIds.has(node.id)),
    edges: graph.edges.filter((edge) => shownIds.has(edge.source) && shownIds.has(edge.target)),
    idsConnectingToRoot,
  };
};

const toFilterField = (
  id: OwnershipColumnId,
  cfg: ColumnFilterConfig,
  rows: OwnershipNode[],
): FilterField<OwnershipColumnId> => {
  const label = COLUMN_LABELS[id];
  switch (cfg.type) {
    case "text":
      return { id, label, type: "text" };
    case "number":
      return { id, label, type: "number", suffix: cfg.suffix };
    case "options":
      return { id, label, type: "options", options: cfg.getOptions(rows) };
  }
};

export const ownershipFilterFields = (rows: OwnershipNode[]): FilterField<OwnershipColumnId>[] =>
  ALL_OWNERSHIP_COLUMN_IDS.flatMap((id) => {
    const cfg = COLUMN_FILTER_CONFIGS[id];
    return cfg ? [toFilterField(id, cfg, rows)] : [];
  });
